import { getDefaultPipeexProfileSections } from '../../config/defaultPipeexProfile/defaultPipeexProfile';
import {
  DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES,
  DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES,
  PROCESS_CONDITIONS_FIELD_BY_EDITOR_ROW_ID,
  PROCESS_CONDITIONS_PROFILE_PARAMETER_BY_EDITOR_ROW_ID,
  PROJECT_PROPERTIES_FIELD_BY_EDITOR_ROW_ID,
  PROJECT_PROPERTIES_PROFILE_PARAMETER_BY_EDITOR_ROW_ID,
} from '../../config/mappingScopeEditorFieldDefaults';
import { useConfigurationStore } from '../../stores/useConfigurationStore';
import { buildStatusBuilderWireSection } from './statusBuilderWire';
import { useParameterWorkflowStore } from '../../stores/useParameterWorkflowStore';
import { useQuickFillStore } from '../../stores/useQuickFillStore';
import { useRevitParametersStore } from '../../stores/useRevitParametersStore';
import { useSharedParameterStore } from '../../stores/useSharedParameterStore';
import { useUIStore } from '../../stores/useUIStore';
import type {
  BoltConfiguration,
  MappingAttribute,
  ObjectTypeMapping,
} from '../../types';
import {
  loadCurrentMappingScopeEditorProfileState,
} from './mappingScopeEditorProfileState';
import {
  deriveBoltConfigurationFromProfileMappings,
  getBoltProfileMappingDefinitions,
  getBoltProfileObjectTypeName,
  isBoltProfileMappingAttribute,
  isBoltProfileMappingRow,
  normalizeBoltProfileKey,
} from '../../lib/boltProfileMapping';
import {
  createPipeexUiProfileMetadata,
  normalizeWorkflowOption,
  PIPEEX_UI_PROCESS_FIELD_IDS,
  type PipeexUiProcessFieldId,
  type PipeexUiProcessPropertiesSnapshot,
  type PipeexUiScopeEditorStateSnapshot,
  type PipeexUiWizardProgressSnapshot,
} from './pipeexProfileUiState';
import { SettingsType, type CreatePcfSettingsDto, type PcfSettingsDto } from './pcfSettingsTypes';
import {
  getAppliedAllTags,
  getAppliedValidationMap,
} from '../../stores/pcfProfileCatalogState';
import { buildValidationMapFromStore } from './pcfSettingsValidationBindings';
import {
  convertLegacyIsoArrayToMap,
  getFormFieldValue,
  getTextValue,
  normalizeAttributeType,
  normalizeBoltCalculationType,
  normalizeParameterLookupValue,
  normalizePcfToken,
  normalizeSystemGroupingPreference,
  parseJson,
  resolvePipeexDefaultParameterValue,
  sanitizeResolvedRevitParameterValue,
  toLegacyIsoType,
  type LegacyIsoTypeValue,
  type NamedParameterPair,
  upsertNamedParameter,
} from './pcfSettingsSerialization';

export { buildValidationMapFromStore } from './pcfSettingsValidationBindings';

type ScopeEditorParameterDefinition = {
  rowId: string;
  fieldId: PipeexUiProcessFieldId;
  parameterName: string;
  defaultPcfAttributeName: string;
};

const PROCESS_SCOPE_PARAMETER_DEFINITIONS: ScopeEditorParameterDefinition[] = Object.entries(
  PROCESS_CONDITIONS_FIELD_BY_EDITOR_ROW_ID
).map(([rowId, fieldId]) => ({
  rowId,
  fieldId,
  parameterName:
    PROCESS_CONDITIONS_PROFILE_PARAMETER_BY_EDITOR_ROW_ID[
      rowId as keyof typeof PROCESS_CONDITIONS_PROFILE_PARAMETER_BY_EDITOR_ROW_ID
    ],
  defaultPcfAttributeName:
    DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES[
      rowId as keyof typeof DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES
    ],
}));

const PROJECT_SCOPE_PARAMETER_DEFINITIONS: ScopeEditorParameterDefinition[] = Object.entries(
  PROJECT_PROPERTIES_FIELD_BY_EDITOR_ROW_ID
).map(([rowId, fieldId]) => ({
  rowId,
  fieldId,
  parameterName:
    PROJECT_PROPERTIES_PROFILE_PARAMETER_BY_EDITOR_ROW_ID[
      rowId as keyof typeof PROJECT_PROPERTIES_PROFILE_PARAMETER_BY_EDITOR_ROW_ID
    ],
  defaultPcfAttributeName:
    DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES[
      rowId as keyof typeof DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES
    ],
}));

function buildAvailableParameterNameSet(): Set<string> {
  const available = new Set<string>();
  const parameters = useRevitParametersStore.getState().parameters;

  parameters.forEach((parameter) => {
    if (typeof parameter.name === 'string' && parameter.name.trim().length > 0) {
      available.add(normalizeParameterLookupValue(parameter.name));
    }
    if (typeof parameter.label === 'string' && parameter.label.trim().length > 0) {
      available.add(normalizeParameterLookupValue(parameter.label));
    }
  });

  return available;
}

function buildPipeexUiProcessPropertiesSnapshotFromState(): PipeexUiProcessPropertiesSnapshot {
  const process = useConfigurationStore.getState()
    .processPropertiesConfiguration as unknown as Record<string, unknown>;

  return PIPEEX_UI_PROCESS_FIELD_IDS.reduce<PipeexUiProcessPropertiesSnapshot>((snapshot, fieldId) => {
    snapshot[fieldId] = getFormFieldValue(process[fieldId]);
    return snapshot;
  }, {});
}

function buildPipeexUiWizardProgressSnapshotFromState(): PipeexUiWizardProgressSnapshot {
  const configurationState = useConfigurationStore.getState();
  const uiState = useUIStore.getState();

  return {
    screen: uiState.screen,
    navigationHistory: [...uiState.navigationHistory],
    sections: { ...uiState.sections },
    hasSavedOnce: configurationState.hasSavedOnce,
    isWorkflowApplied: configurationState.isWorkflowApplied,
  };
}

function getScopedProfileParameterName(
  definition: ScopeEditorParameterDefinition,
  scopeEditorState?: PipeexUiScopeEditorStateSnapshot
): string {
  const editorName = scopeEditorState?.pcfAttributeNames?.[definition.rowId]?.trim() || '';
  if (
    !editorName ||
    normalizePcfToken(editorName) === normalizePcfToken(definition.defaultPcfAttributeName)
  ) {
    return definition.parameterName;
  }

  return editorName;
}

function buildScopedParameterPairs({
  defaultEntries,
  scopeEditorState,
  parameterDefinitions,
  processSnapshot,
  availableParameterNames,
  includeDefaults,
}: {
  defaultEntries: unknown[];
  scopeEditorState?: PipeexUiScopeEditorStateSnapshot;
  parameterDefinitions: ScopeEditorParameterDefinition[];
  processSnapshot: PipeexUiProcessPropertiesSnapshot;
  availableParameterNames: Set<string>;
  includeDefaults: boolean;
}): NamedParameterPair[] {
  const pairs: NamedParameterPair[] = [];
  const deletedRowIds = new Set(scopeEditorState?.deletedRowIds ?? []);
  const deletedParameterNames = new Set(
    parameterDefinitions
      .filter(({ rowId }) => deletedRowIds.has(rowId))
      .map(({ parameterName }) => normalizePcfToken(parameterName))
  );
  const replacedParameterNames = new Set(
    parameterDefinitions
      .filter(({ rowId }) => !deletedRowIds.has(rowId))
      .filter(
        (definition) =>
          normalizePcfToken(getScopedProfileParameterName(definition, scopeEditorState)) !==
          normalizePcfToken(definition.parameterName)
      )
      .map(({ parameterName }) => normalizePcfToken(parameterName))
  );

  if (includeDefaults) {
    defaultEntries.forEach((entry) => {
      const defaultName = getTextValue((entry as { Name?: unknown })?.Name);
      const normalizedDefaultName = normalizePcfToken(defaultName);
      if (
        deletedParameterNames.has(normalizedDefaultName) ||
        replacedParameterNames.has(normalizedDefaultName)
      ) {
        return;
      }

      upsertNamedParameter(
        pairs,
        defaultName,
        resolvePipeexDefaultParameterValue(
          getTextValue((entry as { RevitParameter?: unknown })?.RevitParameter),
          availableParameterNames
        )
      );
    });
  }

  parameterDefinitions.forEach((definition) => {
    const { rowId, fieldId } = definition;
    if (deletedRowIds.has(rowId)) {
      return;
    }

    const parameterName = getScopedProfileParameterName(definition, scopeEditorState);
    const parameterValue = sanitizeResolvedRevitParameterValue(
      processSnapshot[fieldId] ?? '',
      availableParameterNames
    );

    // Without the standard set, only rows the user actually mapped belong in
    // the profile — empty bindings would export as meaningless PCF attributes.
    if (!includeDefaults && parameterValue.trim() === '') {
      return;
    }

    upsertNamedParameter(pairs, parameterName, parameterValue);
  });

  (scopeEditorState?.customRows ?? []).forEach((row) => {
    upsertNamedParameter(
      pairs,
      row.label.trim(),
      sanitizeResolvedRevitParameterValue(row.value, availableParameterNames)
    );
  });

  return pairs;
}

function isFormulaProfileMappingRow(attribute: MappingAttribute): boolean {
  // Mirrors the load-side inference in legacyPcfMappingSections: a row is
  // script-driven when its type is Formula or its Revit parameter already
  // carries the "Formula" dispatch marker.
  return (
    (attribute.attributeType || '').trim().toLowerCase() === 'formula' ||
    (attribute.revitParam || '').trim().toLowerCase() === 'formula'
  );
}

function serializeProfileMappingRevitParameter(
  objectTypeName: string,
  attribute: MappingAttribute,
  availableParameterNames: Set<string>
): string {
  // The export engine (IsoParameterCalculator.GetCalculationType) dispatches a
  // mapping to IronPython only when ParName is exactly "Formula". Script rows
  // have no Revit parameter, so without this marker they fall into the plain
  // parameter-read branch and the attribute exports empty.
  if (isFormulaProfileMappingRow(attribute)) {
    return 'Formula';
  }

  const revitParam = attribute.revitParam || '';
  if (isBoltProfileMappingRow(objectTypeName, attribute.pcfAttributeName)) {
    return revitParam;
  }

  return sanitizeResolvedRevitParameterValue(revitParam, availableParameterNames);
}

function findProfileObjectTypeIndex(
  objectTypes: ObjectTypeMapping[],
  objectTypeName: string
): number {
  const normalizedObjectTypeName = normalizeBoltProfileKey(objectTypeName);
  return objectTypes.findIndex(
    (objectType) => normalizeBoltProfileKey(objectType.name) === normalizedObjectTypeName
  );
}

function ensureProfileObjectType(
  objectTypes: ObjectTypeMapping[],
  objectTypeName: string
): ObjectTypeMapping {
  const existingIndex = findProfileObjectTypeIndex(objectTypes, objectTypeName);
  if (existingIndex >= 0) {
    return objectTypes[existingIndex];
  }

  const created: ObjectTypeMapping = { name: objectTypeName, attributes: [] };
  objectTypes.push(created);
  return created;
}

function stripBoltProfileAttributes(objectType: ObjectTypeMapping): ObjectTypeMapping {
  return {
    ...objectType,
    attributes: objectType.attributes
      .filter((attribute) => !isBoltProfileMappingAttribute(attribute))
      .map((attribute, index) => ({ ...attribute, order: index + 1 })),
  };
}

function buildProfileBoltAttributes(bolt: BoltConfiguration): MappingAttribute[] {
  const isBoltBased = getFormFieldValue(bolt.boltModelingChoice) === 'yes';

  return getBoltProfileMappingDefinitions(isBoltBased).map((definition, index) => ({
    order: index + 1,
    pcfAttributeName: definition.pcfAttributeName,
    revitParam: getFormFieldValue(bolt[definition.field]),
    attributeType: definition.attributeType,
    fx: '',
  }));
}

function applyProfileBoltMappings(
  objectTypes: ObjectTypeMapping[],
  bolt: BoltConfiguration
): void {
  const isBoltBased = getFormFieldValue(bolt.boltModelingChoice) === 'yes';
  const targetCategory = getBoltProfileObjectTypeName(isBoltBased);
  const sourceCategory = getBoltProfileObjectTypeName(!isBoltBased);

  const sourceIndex = findProfileObjectTypeIndex(objectTypes, sourceCategory);
  if (sourceIndex >= 0) {
    objectTypes[sourceIndex] = stripBoltProfileAttributes(objectTypes[sourceIndex]);
  }

  const targetObjectType = ensureProfileObjectType(objectTypes, targetCategory);
  const targetIndex = findProfileObjectTypeIndex(objectTypes, targetCategory);
  const nonBoltAttributes = targetObjectType.attributes.filter(
    (attribute) => !isBoltProfileMappingAttribute(attribute)
  );
  const mergedAttributes = [...nonBoltAttributes, ...buildProfileBoltAttributes(bolt)].map(
    (attribute, index) => ({ ...attribute, order: index + 1 })
  );

  objectTypes[targetIndex] = {
    ...targetObjectType,
    attributes: mergedAttributes,
  };
}

export function buildPcfSettingsDtoFromStore(
  settingsType: SettingsType = SettingsType.User
): PcfSettingsDto {
  const defaultSections = getDefaultPipeexProfileSections();
  const state = useConfigurationStore.getState();
  const parameterWorkflowState = useParameterWorkflowStore.getState();
  const quickFillState = useQuickFillStore.getState();
  const sharedParameterState = useSharedParameterStore.getState();
  const selectedWorkflowOption = useUIStore.getState().selectedOption;
  const processSnapshot = buildPipeexUiProcessPropertiesSnapshotFromState();
  const progressSnapshot = buildPipeexUiWizardProgressSnapshotFromState();
  const scopeEditorState = loadCurrentMappingScopeEditorProfileState();
  const {
    menuConfiguration: menu,
    connectorBindingConfiguration: connector,
    processPropertiesConfiguration: process,
    mappingConfiguration: mapping,
    boltConfiguration: bolt,
    additionalCategoriesConfiguration: additionalCategories,
  } = state;
  const availableParameterNames = buildAvailableParameterNameSet();

  const defaultBomParameters = parseJson<any>(defaultSections.bomParameters, {});
  const defaultProjectParameters = Array.isArray(defaultBomParameters?.ProjectParameters)
    ? defaultBomParameters.ProjectParameters
    : [];
  const defaultSystemParameters = Array.isArray(defaultBomParameters?.SystemParameters)
    ? defaultBomParameters.SystemParameters
    : [];
  // A metadata group the user turned off on the properties step (or in the
  // mapping workspace) must not leak into the saved profile: its parameters
  // would still be exported even though the UI reports the group as "Off".
  // `includeDefaultPipeexParameters` (the landing prompt on the properties
  // step) decides whether enabled groups carry the shipped PipeEX standard
  // parameter set or only the rows the user actually mapped. `null` means the
  // prompt has not been answered yet — keep the legacy full set then.
  const includeDefaultParameters = process.includeDefaultPipeexParameters !== false;
  const projectPairs = process.projectPropertiesChecked
    ? buildScopedParameterPairs({
        defaultEntries: defaultProjectParameters,
        scopeEditorState: scopeEditorState.projectProperties,
        parameterDefinitions: PROJECT_SCOPE_PARAMETER_DEFINITIONS,
        processSnapshot,
        availableParameterNames,
        includeDefaults: includeDefaultParameters,
      })
    : [];
  const systemPairs = process.processConditionsChecked
    ? buildScopedParameterPairs({
        defaultEntries: defaultSystemParameters,
        scopeEditorState: scopeEditorState.processConditions,
        parameterDefinitions: PROCESS_SCOPE_PARAMETER_DEFINITIONS,
        processSnapshot: {
          ...processSnapshot,
          pipingSpecValuePC: processSnapshot.pipingSpecValuePC || menu.pipingSpecValue || '',
        },
        availableParameterNames,
        includeDefaults: includeDefaultParameters,
      })
    : [];

  const projectParameters = projectPairs
    .filter((entry) => entry.name.trim() !== '')
    .map((entry, idx) => ({
      Name: entry.name || '',
      RevitParameter: entry.value || '',
      Id: { Value: idx + 1 },
      ParameterType: 1,
      POrder: idx + 1,
    }));

  const systemParameters = systemPairs
    .filter((entry) => entry.name.trim() !== '')
    .map((entry, idx) => ({
      Name: entry.name || '',
      RevitParameter: entry.value || '',
      Id: { Value: idx + 1001 },
      ParameterType: 2,
      POrder: idx + 1,
    }));

  const objectTypes = (mapping.general.mappingTable.objectType || []).map((objectType) => ({
    ...objectType,
    attributes: [...(objectType.attributes || [])],
  }));
  const profileBolt = deriveBoltConfigurationFromProfileMappings(bolt, objectTypes);

  const ensureObjectType = (name: string) => {
    const existing = objectTypes.find((objectType) => objectType.name === name);
    if (existing) {
      return existing;
    }

    const created = { name, attributes: [] as any[] };
    objectTypes.push(created);
    return created;
  };

  const defaultIsoEntries = parseJson<any[]>(defaultSections.isoParameters, []);
  const defaultIsoMap = convertLegacyIsoArrayToMap(defaultIsoEntries);

  Object.entries(defaultIsoMap).forEach(([objectTypeName, attributes]) => {
    const targetObjectType = ensureObjectType(objectTypeName);

    attributes.forEach((attribute: any) => {
      const pcfAttributeName = getTextValue(attribute?.pcfName);
      if (!pcfAttributeName.trim()) {
        return;
      }

      const normalizedName = pcfAttributeName.trim().toLowerCase();
      const defaultParName = getTextValue(attribute?.parName);
      // "Formula" is the export engine's IronPython dispatch marker, not a real
      // Revit parameter — the availability check below would blank it and
      // silently disable built-in script rows on every profile save.
      const defaultIsFormula = defaultParName.trim().toLowerCase() === 'formula';
      const defaultRevitParam = defaultIsFormula
        ? 'Formula'
        : resolvePipeexDefaultParameterValue(defaultParName, availableParameterNames);
      const defaultAttributeType = defaultIsFormula
        ? 'Formula'
        : normalizeAttributeType(attribute?.isoType);
      const defaultFx = getTextValue(attribute?.script);
      const existingIndex = targetObjectType.attributes.findIndex(
        (candidate) => (candidate.pcfAttributeName || '').trim().toLowerCase() === normalizedName
      );

      if (existingIndex >= 0) {
        const existing = targetObjectType.attributes[existingIndex];
        const existingRevitParam =
          typeof existing.revitParam === 'string' ? existing.revitParam : '';

        targetObjectType.attributes[existingIndex] = {
          ...existing,
          pcfAttributeName,
          revitParam:
            existingRevitParam.trim().length > 0 ? existingRevitParam : defaultRevitParam,
          attributeType: existing.attributeType || defaultAttributeType,
          fx:
            typeof existing.fx === 'string' && existing.fx.trim().length > 0
              ? existing.fx
              : defaultFx,
        };
        return;
      }

      const nextOrder = targetObjectType.attributes.reduce((maxOrder, candidate) => {
        const order = Number(candidate.order ?? 0);
        return Number.isFinite(order) ? Math.max(maxOrder, order) : maxOrder;
      }, 0) + 1;

      targetObjectType.attributes.push({
        order: nextOrder,
        pcfAttributeName,
        revitParam: defaultRevitParam,
        attributeType: defaultAttributeType,
        fx: defaultFx,
      });
    });
  });

  const upsertAllAttribute = (
    pcfAttributeName: string,
    revitParam: string,
    attributeType: 'Parameter' | 'Attribute' | 'BomColumn' | 'Formula',
    // The menu page owns the SKEY / OBJECT-TYPE / PIPING-SPEC bindings — the
    // mapping-table sanitizer keeps these rows out of the editable grid, so
    // the only "existing" rows they can collide with here are the factory
    // defaults merged above. Those must never win over the menu values:
    // letting them through is what made profiles export '#PCF_SKEY' while
    // the UI, QuickFill, and the model setup all used the menu bindings.
    overrideExisting = false
  ) => {
    const allObjectType = ensureObjectType('ALL');
    const normalizedName = pcfAttributeName.trim().toLowerCase();
    if (!normalizedName) {
      return;
    }

    const incomingRevitParam = revitParam ?? '';
    const hasIncomingRevitParam = incomingRevitParam.trim().length > 0;
    const existingIndex = allObjectType.attributes.findIndex(
      (attr) => (attr.pcfAttributeName || '').trim().toLowerCase() === normalizedName
    );

    if (existingIndex >= 0) {
      const existing = allObjectType.attributes[existingIndex];
      const existingRevitParam = typeof existing.revitParam === 'string' ? existing.revitParam : '';
      const hasExistingRevitParam = existingRevitParam.trim().length > 0;
      const nextRevitParam = overrideExisting && hasIncomingRevitParam
        ? incomingRevitParam
        : hasExistingRevitParam
          ? existingRevitParam
          : incomingRevitParam;
      const nextAttributeType = existing.attributeType || attributeType;

      allObjectType.attributes[existingIndex] = {
        ...existing,
        pcfAttributeName,
        revitParam: nextRevitParam,
        attributeType: nextAttributeType,
      };
      return;
    }

    if (!hasIncomingRevitParam) {
      return;
    }

    const nextOrder = allObjectType.attributes.reduce((maxOrder, attr) => {
      const order = Number(attr.order ?? 0);
      return Number.isFinite(order) ? Math.max(maxOrder, order) : maxOrder;
    }, 0) + 1;

    allObjectType.attributes.push({
      order: nextOrder,
      pcfAttributeName,
      revitParam: incomingRevitParam,
      attributeType,
      fx: '',
    });
  };

  upsertAllAttribute('OBJECT-TYPE', menu.objectTypeValue || '', 'Parameter', true);
  upsertAllAttribute('SKEY', menu.sKeyValue || '', 'Parameter', true);
  upsertAllAttribute('PIPING-SPEC', sanitizeResolvedRevitParameterValue(menu.pipingSpecValue || getFormFieldValue(process.pipingSpecValuePC), availableParameterNames), 'Parameter', true);
  upsertAllAttribute('END-POINT_1', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.endpoint1Value), availableParameterNames), 'Parameter');
  upsertAllAttribute('END-POINT_2', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.endpoint2Value), availableParameterNames), 'Parameter');
  upsertAllAttribute('END-POINT_3', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.endpoint3Value), availableParameterNames), 'Parameter');
  upsertAllAttribute('END-POINT_4', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.endpoint4Value), availableParameterNames), 'Parameter');
  upsertAllAttribute('PressureClass', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.pressureClassValue), availableParameterNames), 'BomColumn');
  upsertAllAttribute('Material', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.materialValue), availableParameterNames), 'BomColumn');
  upsertAllAttribute('DesignStd', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.designStdValue), availableParameterNames), 'BomColumn');
  upsertAllAttribute('INSULATION-SPEC', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.insulationSpecValue), availableParameterNames), 'Parameter', true);
  upsertAllAttribute('TRACING-SPEC', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.tracingSpecValue), availableParameterNames), 'Parameter', true);
  upsertAllAttribute('WEIGHT', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.weightValue), availableParameterNames), 'Parameter', true);
  upsertAllAttribute('INSULATIONTHICKNESS', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.insulationThicknessValue), availableParameterNames), 'Attribute', true);
  upsertAllAttribute('ITEM-DESCRIPTION', sanitizeResolvedRevitParameterValue(getFormFieldValue(connector.itemDescriptionValue), availableParameterNames), 'Parameter');

  applyProfileBoltMappings(objectTypes, profileBolt);

  const isoParameterEntries: Array<{
    Id: { Value: number };
    PcfName: { Value: string };
    ParName: { Value: string };
    ObjectType: { Value: string };
    Script: { Value: string };
    IsoType: LegacyIsoTypeValue;
    CSort: string;
  }> = [];

  for (const objectType of objectTypes) {
    for (const attr of objectType.attributes || []) {
      const rawOrder = Number(attr.order ?? 0);
      const normalizedOrder = Number.isFinite(rawOrder) && rawOrder > 0 ? rawOrder : -1;
      isoParameterEntries.push({
        Id: { Value: normalizedOrder },
        PcfName: { Value: attr.pcfAttributeName || '' },
        ParName: {
          Value: serializeProfileMappingRevitParameter(
            objectType.name || 'ALL',
            attr,
            availableParameterNames
          ),
        },
        ObjectType: { Value: objectType.name || 'ALL' },
        Script: { Value: attr.fx || '' },
        IsoType: toLegacyIsoType(attr.attributeType),
        CSort: normalizedOrder > 0 ? String(normalizedOrder).padStart(2, '0') : '',
      });
    }
  }

  const bomColumnParameters = (ensureObjectType('ALL').attributes || [])
    .filter((attr) => String(attr.attributeType || '').toLowerCase() === 'bomcolumn')
    .map((attr, idx) => ({
      Name: attr.pcfAttributeName || '',
      RevitParameter: sanitizeResolvedRevitParameterValue(
        attr.revitParam || '',
        availableParameterNames
      ),
      Id: { Value: idx + 2001 },
      ParameterType: 3,
      POrder: Number(attr.order ?? idx + 1),
    }));

  const legacyBoltCalculationType = normalizeBoltCalculationType(
    profileBolt.boltBaseFamilyValue.value,
    'GasketBased'
  ) === 'BoltBased' ? 1 : 0;
  const centerOfCoordinates =
    mapping.advanced.centerOfCoordinates === 'SurveyPoint'
      ? 'SurveyPoint'
      : 'ProjectBasePoint';

  // Status Builder: PascalCase wire section (mirrors the C# StatusBuilderSettings
  // contract). Omitted entirely when no category carries a valid row — the
  // pipeline treats an absent section as "feature off".
  const statusBuilderWire = buildStatusBuilderWireSection(
    useConfigurationStore.getState().statusBuilderConfiguration
  );

  const systemGroupingPreference = normalizeSystemGroupingPreference(menu.systemGroupingValue, 'BySystemName');
  // Parameter grouping rides the opaque advanced_settings JSON as a PascalCase
  // GroupingParameter section the C# pipeline reads; omitted for system grouping.
  const groupingParameterWire = systemGroupingPreference === 'ByParameter'
    ? {
        ParameterName: menu.groupingParameterName || '',
        Source: menu.groupingParameterSource === 'Type' ? 'Type' : 'Element',
      }
    : undefined;

  const advancedSettings = {
    base: JSON.stringify({
      IsBoltParameter: profileBolt.boltSetActiveValue.value || '',
      BoltCalculationType: legacyBoltCalculationType,
      SystemGroupingPreference: systemGroupingPreference,
      CenterOfCoordinates: centerOfCoordinates,
      ...(groupingParameterWire ? { GroupingParameter: groupingParameterWire } : {}),
      ...(statusBuilderWire ? { StatusBuilder: statusBuilderWire } : {}),
    }),
    pipeexUi: JSON.stringify(
      createPipeexUiProfileMetadata(
        normalizeWorkflowOption(selectedWorkflowOption ?? menu.selectedOption),
        {
          processConditionsChecked: process.processConditionsChecked,
          projectPropertiesChecked: process.projectPropertiesChecked,
        },
        processSnapshot,
        quickFillState.properties,
        quickFillState.extraProperties,
        menu,
        connector,
        scopeEditorState,
        {
          parameterWorkflow: {
            ...parameterWorkflowState,
            sharedParameterFileContent:
              sharedParameterState.useDefaultFile ||
              typeof sharedParameterState.fileContent !== 'string' ||
              sharedParameterState.fileContent.trim().length === 0
                ? undefined
                : sharedParameterState.fileContent,
          },
          menu,
          connector,
          bolt: profileBolt,
          additionalCategories,
          process,
          mapping: { advanced: mapping.advanced, objectTypes },
          progress: progressSnapshot,
        }
      )
    ),
  };

  return {
    advancedSettings: JSON.stringify(advancedSettings),
    allTags: getAppliedAllTags(defaultSections.allTags),
    bomParameters: JSON.stringify({
      ProjectParameters: projectParameters,
      SystemParameters: systemParameters,
      BomParameters: bomColumnParameters,
    }),
    isoParameters: JSON.stringify(isoParameterEntries),
    validationMap: getAppliedValidationMap(JSON.stringify(buildValidationMapFromStore())),
    settingsType,
  };
}

export function buildDefaultPipeexSettingsDto(
  settingsType: SettingsType = SettingsType.User
): CreatePcfSettingsDto {
  const defaults = getDefaultPipeexProfileSections();

  return {
    advancedSettings: defaults.advancedSettings,
    allTags: defaults.allTags,
    bomParameters: defaults.bomParameters,
    isoParameters: defaults.isoParameters,
    validationMap: defaults.validationMap,
    settingsType,
  };
}
